import { API_INSTANCE, fullBuildURL } from 'src/features/utils';
import {
  SeatingChart,
  CreateSeatingChartPayload,
  UpdateSeatingChartPayload,
  UpdateTicketAssignmentsPayload,
  SeatCategory,
} from '../types';

/**
 * Create a new seating chart (standalone or event-specific)
 */
export const createSeatingChartApi = async (payload: CreateSeatingChartPayload) => {
  if (payload.eventId) {
    // Event-specific chart creation
    return await API_INSTANCE.post(
      fullBuildURL(`/api/v1/dashboard/seating-charts/events/${payload.eventId}`),
      payload,
    );
  } else {
    // Standalone chart creation
    return await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard/seating-charts`), payload);
  }
};

/**
 * Get all seating charts for the current site
 */
export const getSiteSeatingChartsApi = async () =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/seating-charts`));

/**
 * Get seating chart for an event
 */
export const getEventSeatingChartApi = async (eventId: string) =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/seating-charts/events/${eventId}`));

/**
 * Update seating chart
 */
export const updateSeatingChartApi = async (payload: UpdateSeatingChartPayload) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/seating-charts/${payload.chartId}`),
    payload.updates,
  );

/**
 * Delete seating chart
 */
export const deleteSeatingChartApi = async (chartId: string) =>
  await API_INSTANCE.delete(fullBuildURL(`/api/v1/dashboard/seating-charts/${chartId}`));

/**
 * Update ticket assignments to seat categories
 */
export const updateTicketAssignmentsApi = async (payload: UpdateTicketAssignmentsPayload) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/seating-charts/${payload.chartId}/assignments`),
    { ticket_assignments: payload.ticket_assignments },
  );

/**
 * Get chart categories from seats.io
 */
export const getChartCategoriesApi = async (chartId: string) =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/seating-charts/${chartId}/categories`));

/**
 * Get designer token for embedding chart designer
 */
export const getDesignerTokenApi = async (chartId: string) =>
  await API_INSTANCE.get(
    fullBuildURL(`/api/v1/dashboard/seating-charts/${chartId}/designer-token`),
  );

/**
 * Get list of saved templates for a site
 */
export const getTemplatesApi = async (siteId: string) =>
  await API_INSTANCE.get(
    fullBuildURL(`/api/v1/dashboard/seating-charts/templates`, `site_id=${siteId}`),
  );

/**
 * Save a seating chart as a template
 */
export const saveAsTemplateApi = async (chartId: string, templateName: string) =>
  await API_INSTANCE.post(
    fullBuildURL(`/api/v1/dashboard/seating-charts/${chartId}/save-as-template`),
    { template_name: templateName },
  );

/**
 * Get pre-built template charts from template workspace
 */
export const getTemplateChartsApi = async () =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/seating-charts/template-charts`));
